import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import { get, post } from "helpers/api_helper"; // Assuming you have this helper for API calls
import "./projects.scss";
import * as api from "../../constants/api_constants";
import CustomPagination from "components/CustomPagination";
import SummaryCards from "components/SummaryCards";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { formattedDate } from "helpers/utils/dateUtils";
import { formatToPascal } from "helpers/utils/nameUtils";

function Projects() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [projectsData, setProjectsData] = useState([]);
  const [projectsCount, setProjectsCount] = useState({});
  const [customers, setCustomers] = useState([]);
  const [apiStatus, setApiStatus] = useState({ loading: false, error: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    sortBy: "projectCode",
    direction: "DESC",
  });
  const timerRef = useRef(null);

  const fetchProjects = useCallback(
    async (query, page = 1) => {
      setApiStatus((prev) => ({ ...prev, loading: true }));
      try {
        const response = await post(api.GET_ALL_PROJECTS, {
          page: page - 1,
          pageSize: 10,
          sortBy: sortConfig.sortBy,
          direction: sortConfig.direction,
          searchKeyword: query,
          projectDTO: {
            subscriptionId: 100,
          },
        });

        setProjectsData(response?.paginationResponse?.data || []);
        setTotalPages(response?.paginationResponse?.totalPages || 0);
      } catch (error) {
        setApiStatus({ loading: false, error: error.message });
      } finally {
        setApiStatus((prev) => ({ ...prev, loading: false }));
      }
    },
    [sortConfig]
  );

  const fetchCustomersData = useCallback(async () => {
    try {
      const custCountData = await get(api.GET_CUSTOMERS_COUNT_DATA);

      const customersData = await post(api.GET_ALL_CUSTOMERS_BY_STATUS, {
        customerTO: {
          subscriptionId: 100,
          status: 1,
        },
        pageSize: custCountData.totalCount,
        page: 0,
        direction: "DESC",
        sortBy: "customerId",
      });

      const updatedCustData = customersData.customerPageResponse.data.map(
        (item) => ({
          value: item.customerId,
          label: item.companyName,
        })
      );
      setCustomers(updatedCustData);
    } catch (error) {
      console.log("Error Fetching data");
    }
  }, []);

  const fetchCount = useCallback(async () => {
    try {
      const response = await get(api.GET_ALL_PROJECTS_BY_STATUS);
      setProjectsCount(response.paginationResponse.data[0]);
      // console.log(projectsCount)
    } catch (error) {
      setApiStatus((prev) => ({ ...prev, error: error.message }));
    }
  }, []);

  const doFetchCalls = useCallback(async () => {
    await fetchCustomersData();
    await fetchCount();
    await fetchProjects();
  }, [fetchCustomersData, fetchCount, fetchProjects]);

  const handleSort = useCallback((columnName) => {
    setSortConfig((prevConfig) => {
      // If sorting the same column, toggle direction
      if (prevConfig.sortBy === columnName) {
        return {
          sortBy: columnName,
          direction: prevConfig.direction === "ASC" ? "DESC" : "ASC",
        };
      }
      // If sorting a new column, default to ASC
      return {
        sortBy: columnName,
        direction: "ASC",
      };
    });
  }, []);

  const SortArrows = ({ column, currentSort }) => {
    const isCurrentColumn = currentSort.sortBy === column;
    return (
      <span className="sort-arrows">
        <i
          className={`fas fa-sort-up ${
            isCurrentColumn && currentSort.direction === "ASC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
        <i
          className={`fas fa-sort-down ${
            isCurrentColumn && currentSort.direction === "DESC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
      </span>
    );
  };

  // Mapping of table columns to API sort keys
  const columnSortMap = {
    "Project Code": "projectCode",
    "Project Name": "projectName",
    Customer: "customerId",
    "Start Date": "startDate",
    "End Date": "endDate",
    Priority: "priority",
    "Project Manager": "projectManagerName",
    Status: "status",
  };

  const renderTableHeader = () => (
    <thead>
      <tr>
        {[
          "Project Code",
          "Project Name",
          "Customer",
          "Start Date",
          "End Date",
          "Priority",
          "Project Manager",
          "Status",
          "Actions",
        ].map((header) =>
          header !== "Actions" ? (
            <th
              key={header}
              onClick={() => handleSort(columnSortMap[header])}
              style={{ cursor: "pointer" }}
            >
              {header}
              <SortArrows
                column={columnSortMap[header]}
                currentSort={sortConfig}
              />
            </th>
          ) : (
            <th key={header}>{header}</th>
          )
        )}
      </tr>
    </thead>
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchProjects(searchQuery, page);
    },
    [fetchProjects, searchQuery]
  );

  const handleSearchQuery = useCallback(
    (e) => {
      const query = e.target.value;
      setSearchQuery(query);

      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => {
        fetchProjects(query);
      }, 300);
    },
    [fetchProjects]
  );

  const getStatusLabelAndColor = useMemo(() => {
    return (status) => {
      switch (status) {
        case "YET_TO_START":
          return { label: "Yet to Start", color: "text-danger" };
        case "COMPLETED":
          return { label: "Completed", color: "text-info" };
        case "ON_HOLD":
          return { label: "On Hold", color: "text-warning" };
        case "IN_PROGRESS":
          return { label: "In Progress", color: "text-primary" };
        case "DELAYED":
          return { label: "Delayed", color: "text-danger" };
        default:
          return { label: "Unknown", color: "text-muted" };
      }
    };
  }, []);

  const getCustomerName = useCallback(
    (customerId) => {
      const customer = customers.find((cust) => cust.value === customerId);
      return customer ? customer.label : "N/A";
    },
    [customers]
  );

  const handleEdit = useCallback(
    (projectId) => {
      navigate(`/add-project/${projectId}`);
    },
    [navigate]
  );

  useEffect(() => {
    doFetchCalls();
  }, [doFetchCalls]);

  useEffect(() => {
    fetchProjects(searchQuery, currentPage);
  }, [sortConfig, fetchProjects, searchQuery, currentPage]);

  const summaryData = [
    {
      iconClass: "fas fa-clipboard-check text-primary",
      label: "Total Count",
      value: projectsCount.activeProjects,
    },
    {
      iconClass: "fas fa-hourglass-start text-warning",
      label: "Yet to Start",
      value: projectsCount.yet_TO_START,
    },
    {
      iconClass: "fas fa-spinner text-info",
      label: "In Progress",
      value: projectsCount.in_PROGRESS,
    },
    {
      iconClass: "fas fa-pause-circle text-primary",
      label: "On Hold",
      value: projectsCount.on_HOLD,
    },
    {
      iconClass: "fas fa-exclamation-circle text-danger",
      label: "Delayed Count",
      value: projectsCount.delayed,
    },
    {
      iconClass: "fas fa-check-circle text-primary",
      label: "Completed Count",
      value: projectsCount.completed,
    },
  ];

  return (
    <div className="page-content">
      <Container fluid={true} className="py-0 px-0">
        <PageSubHeader
          heading={"Projects"}
          routePath={"Home"}
          pageName={"Projects"}
        />

        <Row className="px-4">
          <SummaryCards data={summaryData} />

          <Row className="py-3 px-4">
            <Col>
              <Row className="d-flex justify-content-between align-items-center py-2 mb-2">
                <Col xs={3}>
                  <InputGroup size="sm" className="elbert-search-group">
                    <Input
                      placeholder="Search projects"
                      className="elbert-search"
                      value={searchQuery}
                      onChange={handleSearchQuery}
                    />
                    <InputGroupText>
                      <span className="fa fa-search"></span>
                    </InputGroupText>
                  </InputGroup>
                </Col>
                <Col xs={3}></Col>
                <Col
                  xs={2}
                  className="d-flex align-items-center justify-content-end"
                >
                  <CustomButton
                    text={"+ Add New"}
                    onClick={() => navigate("/add-project")}
                  />
                </Col>
              </Row>

              <Row>
                <Card>
                  <CardBody>
                    <div className="table-responsive">
                      <Table className="table mb-0">
                        <colgroup>
                          <col style={{ width: "10%" }} /> {/* Project Code */}
                          <col style={{ width: "15%" }} /> {/* Project Name */}
                          <col style={{ width: "15%" }} /> {/* Customer Name */}
                          <col style={{ width: "12%" }} /> {/* Start Date */}
                          <col style={{ width: "12%" }} /> {/* End Date */}
                          <col style={{ width: "10%" }} /> {/* Priority */}
                          <col style={{ width: "12%" }} />{" "}
                          {/* Project Manager */}
                          <col style={{ width: "8%" }} /
                          > {/* Status */}
                          <col style={{ width: "6%" }} /> {/* Actions */}
                        </colgroup>
                        {renderTableHeader()}
                        <tbody>
                          {projectsData.map((project, index) => {
                            const { label, color } = getStatusLabelAndColor(
                              project.status
                            );
                            return (
                              <tr key={index}>
                                {/* {console.log(projectsData)} */}
                                <td>{project.projectCode}</td>
                                <td>{project.projectName}</td>
                                <td>{getCustomerName(project.customerId)}</td>
                                <td>
                                  {formattedDate(
                                    project.projectDetailsTO.startDate
                                  )}
                                </td>
                                <td>
                                  {formattedDate(
                                    project.projectDetailsTO.endDate
                                  )}
                                </td>
                                <td>
                                  {formatToPascal(
                                    project.projectDetailsTO.priority
                                  )}
                                </td>
                                <td>
                                  {
                                    project.projectDetailsTO
                                      .projectTeamTOList[0]?.projectManagerName
                                  }
                                </td>
                                <td className={color}>{label}</td>
                                <td>
                                  <div className="d-flex gap-3 align-items-center">
                                    <i
                                      onClick={() =>
                                        navigate(
                                          `/view-project/${project.projectId}`
                                        )
                                      }
                                      className="fa-solid fa-eye action-icon"
                                      title="View"
                                    ></i>
                                    <i
                                      onClick={() =>
                                        handleEdit(project.projectId)
                                      }
                                      className="fa-regular fa-pen-to-square action-icon"
                                      title="Edit"
                                    ></i>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>

                      {/* {apiStatus.loading ? (
                        <p className="text-center m-3">Loading...</p>
                      ) : null}

                      {!projectsData.length > 0 && !apiStatus.loading && (
                        <p className="text-center m-3">
                          No projects data available
                        </p>
                      )}

                      {apiStatus.error && (
                        <p className="text-danger text-center m-3">
                          {apiStatus.error}
                        </p>
                      )} */}
                    </div>
                  </CardBody>
                </Card>
              </Row>
            </Col>
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Row>
      </Container>
    </div>
  );
}

export default Projects;
